import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Save, Check, Copy, Trash2, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DirectTextModule({
  order,
  sessionId,
  onConfirm,
  previousParagraphs = [],
  maxParagraphs = 20,
  customSettings,
  initialParagraph = null
}) {
  const [content, setContent] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [paragraphId, setParagraphId] = useState(null);
  const [wordCount, setWordCount] = useState(0);
  
  // Load initial paragraph if provided (from session restore)
  useEffect(() => {
    if (initialParagraph) {
      setContent(initialParagraph.content || '');
      setParagraphId(initialParagraph.id);
      setConfirmed(true);
    }
  }, [initialParagraph]);
  
  // Update word count
  useEffect(() => {
    const words = content.trim().split(/\s+/).filter(w => w.length > 0);
    setWordCount(words.length);
  }, [content]);

  const handleConfirm = () => {
    if (!content.trim()) {
      toast.error('Please enter some text before confirming');
      return;
    }
    
    if (content.trim().length < 50) {
      toast.error('Please enter at least 50 characters');
      return;
    }

    const tempId = `temp-${Date.now()}-${order}`;
    const paragraphData = {
      id: paragraphId || tempId,
      order,
      content: content.trim(),
      session_id: sessionId,
      main_idea: null
    };

    setParagraphId(tempId);
    setConfirmed(true);
    onConfirm(paragraphData);
    toast.success(`Paragraph ${order} confirmed!`);
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset this paragraph?')) {
      setContent('');
      setConfirmed(false);
      toast.info('Paragraph reset');
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard');
  };

  const isDisabled = order > previousParagraphs.length + 1 || (previousParagraphs.length >= maxParagraphs);

  if (isDisabled) {
    return (
      <div className="admin-card opacity-50">
        <div className="text-center text-white py-8">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="text-lg">Complete previous paragraphs first</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`admin-card ${confirmed ? 'border-4 border-green-500' : ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-2xl font-bold text-white flex items-center gap-2">
          <FileText className="w-6 h-6" />
          Paragraph {order} - Direct Text Entry
        </h3>
        {confirmed && (
          <div className="flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg font-bold">
            <Check className="w-5 h-5" />
            CONFIRMED
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="mb-4 p-4 bg-blue-100 border-2 border-blue-400 rounded-lg">
        <p className="text-blue-900 font-semibold">
          ✍️ Write or paste your content directly. AI will only format it into the final publication.
        </p>
      </div>

      {/* Text Area */}
      <div className="mb-4">
        <label className="block text-white font-bold text-lg mb-2">
          Your Content {customSettings.wordCountTarget && `(Target: ${customSettings.wordCountTarget} words)`}
        </label>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          disabled={confirmed}
          placeholder="Type or paste your paragraph here..."
          className="w-full h-64 px-4 py-3 border-2 border-[#8B6F47] rounded focus:outline-none focus:border-[#6B4423] text-gray-900 resize-vertical"
          style={{ minHeight: '256px' }}
        />
        <div className="flex justify-between mt-2 text-sm text-white">
          <span>Characters: {content.length}</span>
          <span>Words: {wordCount}</span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        {!confirmed ? (
          <>
            <button
              onClick={handleConfirm}
              disabled={!content.trim() || content.trim().length < 50}
              className="retro-button flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Check className="w-5 h-5" />
              Confirm Paragraph
            </button>
            <button
              onClick={() => setContent('')}
              disabled={!content}
              className="retro-button flex items-center gap-2 bg-gray-600 hover:bg-gray-700 disabled:opacity-50"
            >
              <Trash2 className="w-5 h-5" />
              Clear
            </button>
          </>
        ) : (
          <>
            <button
              onClick={handleReset}
              className="retro-button flex items-center gap-2 bg-yellow-600 hover:bg-yellow-700"
            >
              <Save className="w-5 h-5" />
              Edit Paragraph
            </button>
            <button
              onClick={handleCopy}
              className="retro-button flex items-center gap-2"
            >
              <Copy className="w-5 h-5" />
              Copy
            </button>
          </>
        )}
      </div>

      {/* Display Confirmed Content */}
      {confirmed && (
        <div className="mt-6 p-4 bg-white rounded border-2 border-green-500">
          <h4 className="font-bold text-[#6B4423] mb-2">Confirmed Content:</h4>
          <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">{content}</p>
        </div>
      )}
    </div>
  );
}
